import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Clarity from "@microsoft/clarity";

/**
 * Consent-gated Google Analytics 4 + Microsoft Clarity. Renders nothing.
 * Rendered once inside PublicLayout (App.js), so it never runs on /admin.
 *
 * IDs come from env vars at BUILD time (see .env.example / .env.production):
 *   REACT_APP_GA4_ID          G-XXXXXXXXXX  (empty → GA4 never loads)
 *   REACT_APP_CLARITY_ID      project id    (empty → Clarity never loads)
 *   REACT_APP_ANALYTICS_DEBUG "true" to also run outside production builds
 *
 * Nothing is requested until the visitor allows the "analytics" category in
 * the cookie banner (CookieConsent.js): "Accept all", or the Analytics switch
 * under "Manage preferences". The stored choice is read on every visit, and
 * the banner's "cookieconsentchange" event starts/stops tracking at once (no
 * reload). Withdrawing consent stops further GA4 hits and tells Clarity.
 *
 * Skipped entirely in automated browsers (navigator.webdriver), e.g. the bot
 * prerender (prerender.js) and the Playwright visual tests.
 */
const GA_ID = (process.env.REACT_APP_GA4_ID || "").trim();
const CLARITY_ID = (process.env.REACT_APP_CLARITY_ID || "").trim();
const HAS_GA = /^G-[A-Z0-9]+$/i.test(GA_ID);
const HAS_CLARITY = /^[a-z0-9]+$/i.test(CLARITY_ID);
const ENV_ALLOWED =
  process.env.NODE_ENV === "production" || process.env.REACT_APP_ANALYTICS_DEBUG === "true";

// Same keys / event as CookieConsent.js.
const CONSENT_KEY = "cookieConsent";
const PREFS_KEY = "cookiePreferences";
const CONSENT_EVENT = "cookieconsentchange";

// Wait for the page <title> (set by react-helmet-async, often after a lazy
// chunk or API call) to stop changing before sending the page_view.
const TITLE_SETTLE_MS = 500;
const TITLE_MAX_WAIT_MS = 3000;

const isAutomated = () =>
  typeof navigator !== "undefined" && navigator.webdriver === true;

// true only when the visitor explicitly allowed analytics cookies.
const readConsent = () => {
  try {
    const prefs = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
    if (prefs && typeof prefs.analytics === "boolean") return prefs.analytics;
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch (e) {
    return false;
  }
};

// Module-level so a remount never injects a script twice or re-counts a page.
let gaLoaded = false;
let clarityLoaded = false;
let lastPagePath = null;

const gtag = function () {
  // gtag.js expects the real `arguments` object, not an array.
  window.dataLayer.push(arguments);
};

const startGa = () => {
  if (!HAS_GA) return;
  window[`ga-disable-${GA_ID}`] = false;
  if (gaLoaded) {
    gtag("consent", "update", { analytics_storage: "granted" });
    return;
  }
  gaLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtag;
  gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("js", new Date());
  // SPA: page views are sent manually on each route change (below), so this
  // code is the ONLY page_view source. Keep GA4 > Data streams > Enhanced
  // measurement > "Page changes based on browser history events" turned OFF,
  // or every route change is counted twice.
  gtag("config", GA_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);
};

// Stop further GA4 hits (consent mode + Google's official opt-out flag).
const stopGa = () => {
  if (!gaLoaded) return;
  gtag("consent", "update", { analytics_storage: "denied" });
  window[`ga-disable-${GA_ID}`] = true;
};

const startClarity = () => {
  if (!HAS_CLARITY) return;
  try {
    if (!clarityLoaded) {
      clarityLoaded = true;
      Clarity.init(CLARITY_ID);
    }
    Clarity.consent(true);
  } catch (e) {
    /* never let analytics break the page */
  }
};

// Clarity's consent API: false clears its cookies and stops tracking.
const stopClarity = () => {
  if (!clarityLoaded) return;
  try {
    Clarity.consent(false);
  } catch (e) {
    /* ignore */
  }
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const [consent, setConsent] = useState(readConsent);
  const allowed =
    consent && !isAutomated() && !location.pathname.startsWith("/admin");

  // Follow the banner (same tab) and other tabs (storage event).
  useEffect(() => {
    const onConsent = (e) => {
      const detail = e && e.detail;
      setConsent(
        detail && typeof detail.analytics === "boolean" ? detail.analytics : readConsent()
      );
    };
    const onStorage = (e) => {
      if (!e.key || e.key === PREFS_KEY || e.key === CONSENT_KEY) setConsent(readConsent());
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onConsent);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Load (or resume) after consent; stop sending data when it's withdrawn.
  useEffect(() => {
    if (allowed) {
      startGa();
      startClarity();
    } else {
      stopGa();
      stopClarity();
    }
  }, [allowed]);

  // Leaving the public layout in-app (e.g. into /admin): pause GA4 hits.
  useEffect(
    () => () => {
      if (gaLoaded) window[`ga-disable-${GA_ID}`] = true;
    },
    []
  );

  // One page_view per route, once the page title has settled. A route left
  // before that (e.g. an instant <Navigate> redirect) is not counted.
  const pagePath = location.pathname + location.search;
  useEffect(() => {
    if (!allowed || !HAS_GA || pagePath === lastPagePath) return undefined;

    let settleTimer;
    let maxTimer;
    let observer;
    const cleanup = () => {
      clearTimeout(settleTimer);
      clearTimeout(maxTimer);
      if (observer) observer.disconnect();
      document.removeEventListener("visibilitychange", onHide);
    };
    const send = () => {
      cleanup();
      if (pagePath === lastPagePath) return;
      lastPagePath = pagePath;
      gtag("event", "page_view", {
        page_path: pagePath,
        page_location: window.location.href,
        page_title: document.title,
      });
    };
    const settle = () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(send, TITLE_SETTLE_MS);
    };
    // Tab hidden / closed before the title settled: send what we have.
    function onHide() {
      if (document.visibilityState === "hidden") send();
    }

    if (typeof MutationObserver !== "undefined") {
      observer = new MutationObserver(settle);
      observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    }
    document.addEventListener("visibilitychange", onHide);
    settle();
    maxTimer = setTimeout(send, TITLE_MAX_WAIT_MS);
    return cleanup;
  }, [allowed, pagePath]);

  return null;
};

// No IDs, or a dev build without REACT_APP_ANALYTICS_DEBUG=true → a no-op
// component: no listeners, no scripts, no network requests.
const Analytics = ENV_ALLOWED && (HAS_GA || HAS_CLARITY) ? AnalyticsTracker : () => null;

export default Analytics;
